/**
 * JoinFamilyTree — warm invite landing page.
 *
 * Route: /join/:slug?email=&token=
 *
 * Flow:
 *   1) On mount, POST /family-trees/{slug}/invites/verify — this marks
 *      the invite as opened so the 5-day follow-up nudge is skipped.
 *   2) Show the tree name + who invited + personal note (if any) +
 *      a big "Join the family" CTA.
 *   3) If signed in as the invited email, POST /invites/accept — the
 *      server adds the user to the tree with the invited role and we
 *      redirect to /family/{slug}.
 *   4) If signed out, prompt to sign in — once signed in, the same
 *      accept flow runs.
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, ArrowRight, CheckCircle2 } from "lucide-react";

import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function JoinFamilyTree() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const email = (params.get("email") || "").trim();
  const token = (params.get("token") || "").trim();
  const nav = useNavigate();
  const { user, loading } = useAuth();

  const [state, setState] = useState({ loading: true, error: null, invite: null });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!slug || !email || !token) {
      setState({ loading: false, error: "This invite link is incomplete.", invite: null });
      return () => {};
    }
    (async () => {
      try {
        const { data } = await http.post(`/family-trees/${slug}/invites/verify`, { email, token });
        if (!cancelled) setState({ loading: false, error: null, invite: data });
      } catch (e) {
        if (!cancelled) setState({
          loading: false,
          error: e?.response?.data?.detail || "This invite link isn't valid anymore.",
          invite: null,
        });
      }
    })();
    return () => { cancelled = true; };
  }, [slug, email, token]);

  // Auto-accept: if the user is signed in as the invited email AND
  // the invite hasn't been accepted yet, redeem it immediately so
  // they don't have to tap the button.
  useEffect(() => {
    if (state.loading || !state.invite || state.invite.already_accepted) return;
    if (!user || !user.email) return;
    if (user.email.trim().toLowerCase() !== email.trim().toLowerCase()) return;
    // Fire the accept flow silently.
    (async () => {
      try {
        const { data } = await http.post(`/family-trees/${slug}/invites/accept`, { email, token });
        toast.success(`Welcome to the ${state.invite?.tree_name}!`);
        nav(`/family/${data?.tree_slug || slug}`);
      } catch { /* silently fall back to manual accept */ }
    })();
  }, [state.loading, state.invite, user, email, token, slug, nav]);

  const accept = async () => {
    if (!user) {
      // Redirect to /login with a next param so we come back and
      // auto-accept once they're signed in.
      const nextUrl = `/join/${slug}?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`;
      nav(`/login?email=${encodeURIComponent(email)}&next=${encodeURIComponent(nextUrl)}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await http.post(`/family-trees/${slug}/invites/accept`, { email, token });
      if (data?.matched) {
        toast.success(`Welcome to the ${state.invite?.tree_name}!`);
      } else {
        toast.info("You're signed in with a different email — opening the tree.");
      }
      nav(`/family/${data?.tree_slug || slug}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't accept invite.");
    } finally {
      setBusy(false);
    }
  };

  if (state.loading || loading) {
    return <div className="max-w-lg mx-auto py-24 px-6 text-center text-sm" data-testid="join-loading">Checking your invite…</div>;
  }
  if (state.error) {
    return (
      <div className="max-w-lg mx-auto py-24 px-6 text-center" data-testid="join-error">
        <div className="text-lg font-semibold mb-2" style={{ color: "var(--wofy-primary)" }}>Invite not found</div>
        <div className="text-sm opacity-80">{state.error}</div>
        <div className="text-xs opacity-60 mt-4">Ask the sender to email you a fresh link.</div>
      </div>
    );
  }
  const inv = state.invite;
  return (
    <div className="max-w-lg mx-auto py-16 px-4 md:px-6" data-testid="join-landing">
      <div
        className="rounded-2xl p-6 md:p-8 shadow-md"
        style={{
          background: "linear-gradient(135deg, #FFFDF8, #FFF4E1)",
          border: "1px solid var(--wofy-border)",
        }}
      >
        <div className="text-[11px] font-mono uppercase tracking-widest mb-2" style={{ color: "var(--wofy-primary)" }}>
          You're invited
        </div>
        <h1 className="text-2xl md:text-3xl font-semibold mb-1" style={{ color: "var(--wofy-primary)" }}>
          {inv.tree_name}
        </h1>
        <div className="text-sm opacity-80 mb-4">
          {inv.sender_name ? <><strong>{inv.sender_name}</strong> added you as a </> : "You were added as a "}
          <span
            className="inline-block px-1.5 py-0.5 rounded text-[11px] font-semibold"
            style={{ background: "rgba(194,65,12,0.10)", color: "var(--wofy-primary)" }}
          >
            {inv.role === "editor" ? "Editor (can edit)" : "Viewer"}
          </span>
        </div>

        {inv.personal_note && (
          <div
            className="mb-5 rounded-lg px-3 py-2 text-sm italic"
            style={{ background: "rgba(178,93,56,0.06)", borderLeft: "3px solid var(--wofy-primary)", color: "#3D2A17" }}
            data-testid="join-personal-note"
          >
            "{inv.personal_note}"
          </div>
        )}

        <p className="text-sm opacity-90 mb-5 flex items-start gap-2">
          <Users className="w-4 h-4 mt-0.5 shrink-0 opacity-70" />
          Tap the button below to open the tree, see everyone, add stories, and follow updates.
        </p>

        {inv.already_accepted ? (
          <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm" style={{ background: "rgba(16,185,129,0.10)", color: "#047857" }}>
            <CheckCircle2 className="w-4 h-4" /> You've already accepted this invite.
            <button
              type="button"
              onClick={() => nav(`/family/${inv.tree_slug || slug}`)}
              className="ml-auto underline"
              data-testid="join-open-tree"
            >
              Open tree
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={accept}
            disabled={busy}
            className="wofy-btn-primary w-full justify-center"
            data-testid="join-accept"
          >
            {busy ? "One moment…" : (user ? `Join the ${inv.tree_name}` : "Sign in to accept invite")}
            <ArrowRight className="w-4 h-4 ml-1" />
          </button>
        )}

        <div className="text-[11px] opacity-60 mt-4 text-center">
          {user && user.email && user.email.toLowerCase() !== email.toLowerCase() && (
            <div className="mb-2 text-red-600">Heads up — you're signed in as {user.email}. Sign out and back in as {email} to accept as yourself.</div>
          )}
          Invited to <strong>{email}</strong>
        </div>
      </div>
    </div>
  );
}
